import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { registerLocaleData, Location } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import {
  UiService,
  ProfileService,
  ModalComponent,
  Profile,
  JournalService,
  LocalesService,
  ConfigurationsService,
  MicrofrontendsService
} from 'core/index';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'lms-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('modalWarningMessage', { static: false }) public modalWarningMessage: ModalComponent;

  public profileSubscription: Subscription;
  public user: any;
  public warningMessage: any;
  public accountPath: any;
  public notAvailableForPhone: boolean = false;
  public envDomainCookie: String;
  public isFirstNavigation: boolean = true;

  // eslint-disable-next-line
  private AVAILABLE_LANGUAGES = ['de', 'en', 'es', 'fr'];
  // eslint-disable-next-line
  private DEFAULT_LANGUAGE = 'en';
  private userLang: string;

  private messageConfigSubscription: Subscription = new Subscription();

  constructor(
    public router: Router,
    public translate: TranslateService,
    public journalService: JournalService,
    public uiService: UiService,
    public profileService: ProfileService,
    public location: Location,
    private localesService: LocalesService,
    private configurationsService: ConfigurationsService,
    private microfrontendsService: MicrofrontendsService
  ) {
    // See main.ts
    const debugLang = sessionStorage.getItem('debug_lang');

    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((routerEvent: RouterEvent) => {
        this.isFirstNavigation = routerEvent instanceof NavigationStart;
        this.accountPath = location.path() === '/accounts/my-account';
      });

    if (screen.width <= 480) {
      this.notAvailableForPhone = true;
    }

    registerLocaleData(localeDe, 'de');
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeEs, 'es');
    registerLocaleData(localeFr, 'fr');

    if (debugLang !== 'labels') {
      translate.setDefaultLang(this.DEFAULT_LANGUAGE);

      let lang = debugLang || localStorage.getItem('backofficelanguage') || navigator.language;
      lang = lang.substr(0, 2);
      if (!this.AVAILABLE_LANGUAGES.includes(lang)) {
        lang = this.DEFAULT_LANGUAGE;
      }
      this.userLang = lang;
      translate.use(lang);
    }

    try {
      this.setDomain();
    } catch (error) {
      console.error(`Can't set domain`);
    }
  }

  public ngOnInit(): void {
    // eslint-disable-next-line
    document.documentElement.lang = this.translate.getBrowserLang();
    this.profileSubscription = this.profileService.getLoggedUser$().subscribe((loggedUser: Profile) => {
      // eslint-disable-next-line
      const urlParams = new URLSearchParams(window.location.search);
      const isMicrofrontend = urlParams.get('microfrontend');
      this.user = loggedUser;

      if (loggedUser) {
        const selectedClientId = loggedUser.selectedClientId ? loggedUser.selectedClientId : null;

        if (!isMicrofrontend) {
          let currentClient = {};
          this.uiService.hideMicrofrontend();

          if (selectedClientId && loggedUser.clients) {
            // get the info of the selectedClient from the loggedUser.clients list
            for (const client of loggedUser.clients) {
              if (client.clientId === selectedClientId) {
                currentClient = client;
                break;
              }
            }
          }

          if (environment.contentful.active) {
            this.activeContentful();
          }
        }
        this.bootstrapMicrofrontends();
      }
    });
  }

  public ngOnDestroy(): void {
    this.profileSubscription.unsubscribe();
    this.messageConfigSubscription.unsubscribe();
  }

  public closeModal(): void {
    this.modalWarningMessage.close();
  }

  private bootstrapMicrofrontends(): void {
    if (this.microfrontendsService.isBootstrapped) {
      this.microfrontendsService.refreshAuthorization();
    } else {
      this.microfrontendsService.bootstrap();
    }
  }

  /**
   * Show modal of cookies
   * @param event
   */
  public showCookiesConfig(event: boolean): void {
    if (event) {
      this.microfrontendsService.fireMicrofrontendEvent('GlCookies', 'showModal');
    }
  }

  /**
   * Active the contentful service
   * @private
   */
  private activeContentful(): void {
    if (environment.contentful.warningMessageActive) {
      this.messageConfigSubscription = this.configurationsService
        .getInfoMessage$(
          environment.contentful.contentTypeIds.warningMessage,
          '',
          '',
          'tituloWarning',
          this.localesService.getBrowserLocale(),
          false,
          false,
          false,
          true,
          'descripcionDeWarning'
        )
        .subscribe((message) => {
          if (message) {
            this.warningMessage = message;
            this.modalWarningMessage.open();
          }
        });
    }
  }

  /**
   * Set the domain and the cookie domain in the app
   * @private
   */
  private setDomain(): void {
    document.domain = environment.DOMAIN;
    this.envDomainCookie = environment.DOMAIN;

    const script = document.createElement('script');
    const startScriptComment = document.createComment('Iframes domain');
    script.innerText = `document.domain='${environment.DOMAIN}'`;

    document.head.appendChild(startScriptComment);
    document.head.appendChild(script);
  }
}
